import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-rezultati-tekem',
  templateUrl: './rezultati-tekem.component.html',
  styleUrls: ['./rezultati-tekem.component.scss']
})
export class RezultatiTekemComponent implements OnInit, OnDestroy {
  categories: any[] = [];
  resultsByCategory: { [category_id: string]: any[] } = {};
  resultsByTeamAndCategory: { [teamKey: string]: any[] } = {};
  teamsList: string[] = [];
  competitionId: string | null = null;
  competitionName: string = '';
  showTeams: boolean = false;
  private interval: any;

  // Auto-scroll (screensaver style)
  private readonly inactivityMs = 0;
  private readonly waitAtBottomMs = 10000;
  private readonly waitAtTopMs = 10000;
  private readonly scrollSpeedPxPerSec = 40; // down
  private readonly resetSpeedPxPerSec = 450; // up (faster reset)
  private lastInteraction = Date.now();
  private autoScrollCheckTimer: any;
  private autoScrollRafId: number | null = null;
  private autoScrollTimeout: any;
  private autoScrolling = false;
  private readonly interactionHandler = (e: Event) => this.onUserInteraction(e);

  private toNum(value: any): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  private normalizeTargetType(targetType: unknown): string {
    return String(targetType || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  isDecimalTarget(row: any): boolean {
    return ['air rifle', 'air rifle teiler', 'pistol decimal'].includes(this.normalizeTargetType(row?.target_type));
  }

  formatSeriesScore(value: any, row?: any): string {
    const score = Number(value);
    if (!Number.isFinite(score)) return '';
    return this.isDecimalTarget(row) ? score.toFixed(1) : Math.round(score).toString();
  }

  formatTotalScore(row: any): string {
    const score = Number(row?.series_sum);
    if (!Number.isFinite(score)) return '';
    return this.isDecimalTarget(row) ? score.toFixed(1) : Math.round(score).toString();
  }

  formatTeamTotal(teamKey: string): string {
    const rows = this.resultsByTeamAndCategory[teamKey] || [];
    const total = this.getTeamTotal(teamKey);
    const useDecimal = rows.some(row => this.showTeilerDetails(row) || this.isDecimalTarget(row));
    return useDecimal ? total.toFixed(1) : Math.round(total).toString();
  }

  private isPistol(row: any): boolean {
    return String(row?.target_type || '').trim().toLowerCase().startsWith('pistol');
  }

  isDecimalPistol(row: any): boolean {
    return String(row?.target_type || '').trim().toLowerCase() === 'pistol decimal';
  }

  showPistolDetails(row: any): boolean {
    return this.isPistol(row);
  }

  private isTeilerCompetition(row: any): boolean {
    return row?.competition_is_teiler === true || Number(row?.competition_is_teiler) === 1;
  }

  showTeilerDetails(row: any): boolean {
    const targetType = String(row?.target_type || '').trim().toLowerCase();
    return this.isTeilerCompetition(row) && targetType.endsWith('teiler');
  }

  isTeilerCategory(categoryId: string | number): boolean {
    return (this.resultsByCategory[String(categoryId)] || []).some(row => this.showTeilerDetails(row));
  }

  private compareResultsRows(a: any, b: any, categoryIsPistol: boolean, competitionIsTeiler: boolean): number {
    if (competitionIsTeiler) {
      const teilerA = this.toNum(a?.teiler_score);
      const teilerB = this.toNum(b?.teiler_score);
      if (teilerA !== teilerB) return teilerA - teilerB;
      return this.toNum(b?.decimal_sum) - this.toNum(a?.decimal_sum);
    }

    const totalA = this.toNum(a?.series_sum);
    const totalB = this.toNum(b?.series_sum);
    if (totalA !== totalB) return totalB - totalA;

    const pistol = categoryIsPistol;
    const ncA = this.toNum(a?.inner_center);
    const ncB = this.toNum(b?.inner_center);

    if (pistol) {
      if (ncA !== ncB) return ncB - ncA;
    }

    // Non-pistol (or further tie-break): compare last series first
    const seriesKeys = ['S60', 'S50', 'S40', 'S30', 'S20', 'S10'];
    for (const key of seriesKeys) {
      const vA = this.toNum(a?.[key]);
      const vB = this.toNum(b?.[key]);
      if (vA !== vB) return vB - vA;
    }

    // Final tie-breaker
    if (!pistol) {
      if (ncA !== ncB) return ncB - ncA;
    }
    return 0;
  }

  private keepLatestResultPerShooter(rows: any[]): any[] {
    const latestByShooter = new Map<string, any>();
    for (const row of rows) {
      const key = String(row?.member_id ?? row?.targetID ?? '');
      const previous = latestByShooter.get(key);
      if (!previous || this.toNum(row?.targetID) > this.toNum(previous?.targetID)) {
        latestByShooter.set(key, row);
      }
    }
    return Array.from(latestByShooter.values());
  }

  getTeamTotal(teamKey: string): number {
    return (this.resultsByTeamAndCategory[teamKey] || [])
      .reduce((total, result) => total + this.toNum(
        this.showTeilerDetails(result) ? result?.decimal_sum : result?.series_sum
      ), 0);
  }

  getTeamInnerCenters(teamKey: string): number {
    return (this.resultsByTeamAndCategory[teamKey] || [])
      .reduce((total, result) => total + this.toNum(result?.inner_center), 0);
  }

  constructor(private http: HttpClient, private route: ActivatedRoute) {}

  ngOnInit() {
    this.competitionId = this.route.snapshot.paramMap.get('competitionId');
    this.loadCompetitionDetails();
    this.refreshDashboard();

    // Refresh results every 5 seconds
    this.interval = setInterval(() => {
      this.refreshDashboard();
    }, 5000);

    // Auto-scroll functionality (starts after inactivity)
    document.addEventListener('pointerdown', this.interactionHandler, { passive: true });
    document.addEventListener('wheel', this.interactionHandler, { passive: true });
    document.addEventListener('touchstart', this.interactionHandler, { passive: true });
    document.addEventListener('keydown', this.interactionHandler);

    this.autoScrollCheckTimer = setInterval(() => {
      this.maybeStartAutoScroll();
    }, 100);

    // Start immediately (since inactivityMs is 0)
    this.maybeStartAutoScroll();
  }

  ngOnDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (this.autoScrollCheckTimer) {
      clearInterval(this.autoScrollCheckTimer);
    }

    this.stopAutoScroll();

    document.removeEventListener('pointerdown', this.interactionHandler);
    document.removeEventListener('wheel', this.interactionHandler);
    document.removeEventListener('touchstart', this.interactionHandler);
    document.removeEventListener('keydown', this.interactionHandler);
  }

  private onUserInteraction(_e: Event) {
    this.lastInteraction = Date.now();
    this.stopAutoScroll();
  }

  private maybeStartAutoScroll() {
    if (this.autoScrolling) return;
    if (Date.now() - this.lastInteraction < this.inactivityMs) return;
    this.startAutoScrollCycle();
  }

  private startAutoScrollCycle() {
    this.autoScrolling = true;
    this.scrollDownThenResetLoop();
  }

  private stopAutoScroll() {
    this.autoScrolling = false;

    if (this.autoScrollRafId !== null) {
      cancelAnimationFrame(this.autoScrollRafId);
      this.autoScrollRafId = null;
    }
    if (this.autoScrollTimeout) {
      clearTimeout(this.autoScrollTimeout);
      this.autoScrollTimeout = null;
    }
  }

  private getMaxScrollTop(): number {
    const scrollHeight = Math.max(
      document.documentElement?.scrollHeight ?? 0,
      document.body?.scrollHeight ?? 0
    );
    return Math.max(0, scrollHeight - window.innerHeight);
  }

  private durationMsForDistance(distancePx: number, speedPxPerSec: number): number {
    const d = Math.abs(distancePx);
    if (d <= 0) return 0;
    const ms = (d / Math.max(1, speedPxPerSec)) * 1000;
    return Math.max(300, Math.min(120000, ms));
  }

  private animateScrollTo(targetTop: number, durationMs: number, onDone: () => void) {
    if (!this.autoScrolling) return;

    const startTop = window.scrollY;
    const delta = targetTop - startTop;
    if (durationMs <= 0 || delta === 0) {
      window.scrollTo(0, targetTop);
      onDone();
      return;
    }

    const startTime = performance.now();
    const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

    const step = (now: number) => {
      if (!this.autoScrolling) return;

      const t = Math.min(1, (now - startTime) / durationMs);
      const eased = easeInOut(t);
      window.scrollTo(0, startTop + delta * eased);

      if (t < 1) {
        this.autoScrollRafId = requestAnimationFrame(step);
      } else {
        this.autoScrollRafId = null;
        onDone();
      }
    };

    this.autoScrollRafId = requestAnimationFrame(step);
  }

  private scrollDownThenResetLoop() {
    if (!this.autoScrolling) return;

    const bottom = this.getMaxScrollTop();
    const downDuration = this.durationMsForDistance(bottom - window.scrollY, this.scrollSpeedPxPerSec);

    this.animateScrollTo(bottom, downDuration, () => {
      if (!this.autoScrolling) return;

      this.autoScrollTimeout = setTimeout(() => {
        if (!this.autoScrolling) return;

        const upDuration = this.durationMsForDistance(window.scrollY, this.resetSpeedPxPerSec);
        this.animateScrollTo(0, upDuration, () => {
          if (!this.autoScrolling) return;
          // Wait at top, then repeat the cycle (down again)
          this.autoScrollTimeout = setTimeout(() => {
            this.scrollDownThenResetLoop();
          }, this.waitAtTopMs);
        });
      }, this.waitAtBottomMs);
    });
  }

  loadCompetitionDetails() {
    if (!this.competitionId) return;
    // Get all competitions and find the one with matching ID
    const token = localStorage.getItem('access_token');
    const unitId = localStorage.getItem('unit_id');
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
    const params = unitId ? new HttpParams().set('unit_id', unitId) : undefined;

    this.http.get<any[]>('https://testapi.ovh/api/strelstvo/competitions', { headers, params }).subscribe(
      data => {
        console.log('All competitions received:', data);
        const competition = data.find(comp => comp.competitionId == this.competitionId);
        if (competition) {
          this.competitionName = competition.competition_name;
          console.log('Competition name found:', this.competitionName);
        } else {
          this.competitionName = `Tekmovanje ${this.competitionId}`;
          console.log('Competition not found, using fallback name');
        }
      },
      err => {
        console.error('Error loading competitions:', err);
        this.competitionName = `Tekmovanje ${this.competitionId}`;
      }
    );
  }

  loadCategories() {
    // Legacy method kept for compatibility.
    // It used to trigger N requests (one per category). Now it just refreshes via the single dashboard call.
    this.refreshDashboard();
  }

  refreshDashboard() {
    if (!this.competitionId) return;

    const params = new HttpParams().set('competitionId', this.competitionId!);
    const token = localStorage.getItem('access_token');
    const unitId = localStorage.getItem('unit_id');
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
    const unitParams = unitId ? params.set('unit_id', unitId) : params;

    this.http.get<any>('https://testapi.ovh/api/strelstvo/competition-dashboard', { params: unitParams, headers }).subscribe(
      data => {
        const categories = Array.isArray(data?.categories) ? data.categories : [];

        // Sort categories by age from youngest to oldest (same as before)
        const ageOrder = ['Kadeti', 'Juniori', 'Seniorji', 'Veterani'];
        this.categories = categories.sort((a: any, b: any) => {
          const indexA = ageOrder.indexOf(a.category_name);
          const indexB = ageOrder.indexOf(b.category_name);
          return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
        });

        // resultsByCategory comes keyed by category_id (as string)
        const incoming = data?.resultsByCategory ?? {};
        this.resultsByCategory = {};
        Object.keys(incoming).forEach((catIdStr: string) => {
          const rows = Array.isArray(incoming[catIdStr])
            ? this.keepLatestResultPerShooter(incoming[catIdStr])
            : [];
          const categoryIsPistol = rows.some((r: any) => this.isPistol(r));
          const competitionIsTeiler = rows.some((r: any) => this.showTeilerDetails(r));
          const sorted = [...rows].sort((a: any, b: any) => this.compareResultsRows(a, b, categoryIsPistol, competitionIsTeiler));
          this.resultsByCategory[catIdStr] = sorted.map((item: any, index: number) => ({
            ...item,
            position: item?.position ?? (index + 1)
          }));
        });

        // Teams results (same shape as /api/strelstvo/teams-results)
        const teamsData = Array.isArray(data?.teamsResults) ? data.teamsResults : [];

        const hasCategoryResults = Object.values(this.resultsByCategory).some(rows => rows.length > 0);
        if (this.categories.length === 0 && !hasCategoryResults) {
          this.categories = teamsData.map((team: any) => {
            const categoryId = this.getTeamCategoryId(team);
            const targets = Array.isArray(team.targets) ? team.targets : [];
            const categoryIsPistol = targets.some((r: any) => this.isPistol(r));
            const competitionIsTeiler = targets.some((r: any) => this.showTeilerDetails(r));
            this.resultsByCategory[categoryId] = [...targets]
              .sort((a: any, b: any) => this.compareResultsRows(a, b, categoryIsPistol, competitionIsTeiler))
              .map((item: any, index: number) => ({ ...item, position: index + 1 }));

            return {
              category_id: categoryId,
              category_name: team.category_name || `Ekipa ${team.teams_number}`
            };
          });
        }

        teamsData.sort((a: any, b: any) => {
          const categoryNameCompare = String(a?.category_name || '').localeCompare(String(b?.category_name || ''), 'sl');
          if (categoryNameCompare !== 0) return categoryNameCompare;

          const totalCompare = this.getTeamRowsTotal(b?.targets) - this.getTeamRowsTotal(a?.targets);
          if (totalCompare !== 0) return totalCompare;

          const tnA = this.toNum(a?.teams_number);
          const tnB = this.toNum(b?.teams_number);
          if (tnA !== tnB) return tnA - tnB; // teams_number (fight/flight number) first
          const catA = this.toNum(a?.category_id);
          const catB = this.toNum(b?.category_id);
          if (catA !== catB) return catA - catB;
          const nameCompare = String(a?.team_name || '').localeCompare(String(b?.team_name || ''), 'sl');
          if (nameCompare !== 0) return nameCompare;
          const clubA = String(a?.club_name || '');
          const clubB = String(b?.club_name || '');
          return clubA.localeCompare(clubB);
        });

        this.resultsByTeamAndCategory = {};
        this.teamsList = [];
        teamsData.forEach((team: any) => {
          const teamKey = `${team.team_name || team.teams_number}_${team.team_name ? '' : team.club_id}_${team.competitionId}_${team.category_id}`;
          this.teamsList.push(teamKey);
          const targets = Array.isArray(team.targets) ? team.targets : [];
          const categoryIsPistol = targets.some((r: any) => this.isPistol(r));
          const competitionIsTeiler = targets.some((r: any) => this.showTeilerDetails(r));
          this.resultsByTeamAndCategory[teamKey] = [...(team.targets || [])]
            .sort((a: any, b: any) => this.compareResultsRows(a, b, categoryIsPistol, competitionIsTeiler))
            .map((item: any, index: number) => ({ ...item, position: index + 1 }));
        });
      },
      _err => {
        this.categories = [];
        this.resultsByCategory = {};
        this.resultsByTeamAndCategory = {};
        this.teamsList = [];
      }
    );
  }

  private getTeamCategoryId(team: any): string {
    return `team-${team.team_name || team.teams_number}-${team.club_id}-${team.competitionId}`;
  }

  private getTeamDiscipline(team: any): string {
    const targetTypes = (Array.isArray(team?.targets) ? team.targets : [])
      .map((target: any) => String(target?.target_type || '').trim().toLowerCase())
      .filter(Boolean);
    const targetType = targetTypes[0] || '';
    if (targetType.startsWith('pistol')) return 'pistol';
    if (targetType.includes('serial')) return 'air-serial';
    if (targetType.includes('rifle')) return 'air-rifle';
    return targetType || 'unknown';
  }

  private getTeamDisciplineRank(discipline: string): number {
    if (discipline === 'air-rifle') return 0;
    if (discipline === 'air-serial') return 1;
    if (discipline === 'pistol') return 2;
    return 3;
  }

  getTeamDisciplineLabel(teamKey: string): string {
    const team = this.resultsByTeamAndCategory[teamKey]?.[0];
    const discipline = this.getTeamDiscipline({ targets: team ? [team] : [] });
    if (discipline === 'air-rifle') return 'Puška';
    if (discipline === 'air-serial') return 'Serijska puška';
    if (discipline === 'pistol') return 'Pištola';
    return 'Ostale discipline';
  }

  private getTeamRowsTotal(rows: any[] | undefined): number {
    return (Array.isArray(rows) ? rows : []).reduce((total, row) => {
      const value = this.showTeilerDetails(row) ? row?.decimal_sum : row?.series_sum;
      return total + this.toNum(value);
    }, 0);
  }

  getTeamCategoryLabel(teamKey: string): string {
    return this.resultsByTeamAndCategory[teamKey]?.[0]?.category_name || 'Kategorija';
  }

  isFirstTeamInCategory(teamKey: string, teamIndex: number): boolean {
    if (teamIndex === 0) return true;
    const previousTeamKey = this.teamsList[teamIndex - 1];
    return this.getTeamCategoryLabel(teamKey) !== this.getTeamCategoryLabel(previousTeamKey);
  }

  loadResultsForAllCategories() {
    // Legacy method kept for compatibility.
    // It used to trigger N requests (one per category). Now it just refreshes via the single dashboard call.
    this.refreshDashboard();
  }

  loadResultsByTeams() {
    // Legacy method kept for compatibility.
    // It used to call a separate endpoint. Now it just refreshes via the single dashboard call.
    this.refreshDashboard();
  }

  downloadAllCategoriesExcel() {
    if (!this.competitionId) return;
    const url = `https://testapi.ovh/api/strelstvo/competition-results-excel?competitionId=${this.competitionId}`;
    const token = localStorage.getItem('access_token');
    const unitId = localStorage.getItem('unit_id');
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
    const params = unitId ? new HttpParams().set('unit_id', unitId) : undefined;

    this.http.get(url, { responseType: 'blob', headers, params }).subscribe(
      (blob: Blob) => {
        saveAs(blob, `rezultati_vse_kategorije.xlsx`);
      },
      err => {
        console.error('Error downloading Excel:', err);
      }
    );
  }
}
